import { useState, useEffect, useReducer } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getWorkout, createWorkout, updateWorkout } from '../api/workouts';
import { getAppleDeveloperToken } from '../api/music';
import { getExercises } from '../api/exercises';
import { getTags } from '../api/tags';
import { addMediaToWorkout, removeMediaFromWorkout } from '../api/media';
import { getTenantSettings } from '../api/tenant';
import TagSelector from '../components/TagSelector';
import WorkoutIntroMediaSection from '../components/WorkoutIntroMediaSection';
import AlertBanner from '../components/common/AlertBanner';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './WorkoutBuilderPage.css';

// ─── helpers ─────────────────────────────────────────────────────────────────
let _uid = 0;
const uid = () => `local_${++_uid}`;

const emptyItem = () => ({ _id: uid(), exercise_id: '', duration: 30, repetitions: null, rest_after: 10, notes: '' });
const emptySet = () => ({ _id: uid(), name: '', repetitions: 1, rest_between_reps: 0, items: [emptyItem()] });
const emptyRound = () => ({ _id: uid(), name: '', repetitions: 1, rest_between_reps: 0, sets: [emptySet()] });
const PHASE_DEFAULT_NAMES = { warmup: 'Aufwärmen', workout: 'Hauptteil', cooldown: 'Abkühlen' };
const emptyPhase = (type = 'workout') => ({ _id: uid(), name: PHASE_DEFAULT_NAMES[type] || 'Phase', type, music_enabled: true, rounds: [emptyRound()] });

const defaultMeta = { name: '', description: '', format: 'custom', duration: '', difficulty: 1, published: false, sounds_enabled: true, apple_music_playlist_id: '', playlist_enabled: false, tag_ids: [], is_global: false, is_free: false };

// ─── reducer ─────────────────────────────────────────────────────────────────
function builderReducer(state, action) {
  switch (action.type) {
    case 'LOAD': return action.payload;
    case 'SET_META': return { ...state, meta: { ...state.meta, ...action.payload } };

    case 'ADD_PHASE': return { ...state, phases: [...state.phases, emptyPhase()] };
    case 'UPDATE_PHASE': return updateIn(state, ['phases'], action.id, (p) => ({ ...p, ...action.payload }));
    case 'REMOVE_PHASE': return { ...state, phases: state.phases.filter((p) => p._id !== action.id) };
    case 'MOVE_PHASE': return { ...state, phases: move(state.phases, action.id, action.dir) };

    case 'ADD_ROUND': return updateIn(state, ['phases'], action.phaseId, (p) => ({ ...p, rounds: [...p.rounds, emptyRound()] }));
    case 'DUPLICATE_ROUND': return updateIn(state, ['phases'], action.phaseId, (p) => ({ ...p, rounds: insertAfter(p.rounds, action.id, cloneRound(p.rounds.find((r) => r._id === action.id))) }));
    case 'UPDATE_ROUND': return updateNested(state, 'phases', action.phaseId, 'rounds', action.id, (r) => ({ ...r, ...action.payload }));
    case 'REMOVE_ROUND': return updateIn(state, ['phases'], action.phaseId, (p) => ({ ...p, rounds: p.rounds.filter((r) => r._id !== action.id) }));
    case 'MOVE_ROUND': return updateIn(state, ['phases'], action.phaseId, (p) => ({ ...p, rounds: move(p.rounds, action.id, action.dir) }));

    case 'ADD_SET': return updateNested(state, 'phases', action.phaseId, 'rounds', action.roundId, (r) => ({ ...r, sets: [...r.sets, emptySet()] }));
    case 'DUPLICATE_SET': return updateNested(state, 'phases', action.phaseId, 'rounds', action.roundId, (r) => ({ ...r, sets: insertAfter(r.sets, action.id, cloneSet(r.sets.find((s) => s._id === action.id))) }));
    case 'UPDATE_SET': return updateDeep(state, action.phaseId, action.roundId, action.id, null, (s) => ({ ...s, ...action.payload }));
    case 'REMOVE_SET': return updateNested(state, 'phases', action.phaseId, 'rounds', action.roundId, (r) => ({ ...r, sets: r.sets.filter((s) => s._id !== action.id) }));
    case 'MOVE_SET': return updateNested(state, 'phases', action.phaseId, 'rounds', action.roundId, (r) => ({ ...r, sets: move(r.sets, action.id, action.dir) }));

    case 'ADD_ITEM': return updateDeep(state, action.phaseId, action.roundId, action.setId, null, (s) => ({ ...s, items: [...s.items, emptyItem()] }));
    case 'UPDATE_ITEM': return updateDeep(state, action.phaseId, action.roundId, action.setId, action.id, (item) => ({ ...item, ...action.payload }));
    case 'REMOVE_ITEM': return updateDeep(state, action.phaseId, action.roundId, action.setId, null, (s) => ({ ...s, items: s.items.filter((i) => i._id !== action.id) }));
    case 'MOVE_ITEM': return updateDeep(state, action.phaseId, action.roundId, action.setId, null, (s) => ({ ...s, items: move(s.items, action.id, action.dir) }));

    default: return state;
  }
}

// immutable helpers
function updateIn(state, [key], id, fn) {
  return { ...state, [key]: state[key].map((item) => item._id === id ? fn(item) : item) };
}
function updateNested(state, parentKey, parentId, childKey, childId, fn) {
  return {
    ...state,
    [parentKey]: state[parentKey].map((parent) =>
      parent._id !== parentId ? parent : {
        ...parent,
        [childKey]: parent[childKey].map((child) => child._id === childId ? fn(child) : child),
      }
    ),
  };
}
function updateDeep(state, phaseId, roundId, setId, itemId, fn) {
  return {
    ...state,
    phases: state.phases.map((phase) =>
      phase._id !== phaseId ? phase : {
        ...phase,
        rounds: phase.rounds.map((round) =>
          round._id !== roundId ? round : {
            ...round,
            sets: round.sets.map((set) =>
              set._id !== setId ? set : (
                itemId ? { ...set, items: set.items.map((item) => item._id === itemId ? fn(item) : item) } : fn(set)
              )
            ),
          }
        ),
      }
    ),
  };
}
// deep-clone helpers for duplicate actions
function cloneItem(item) { return { ...item, _id: uid() }; }
function cloneSet(set) { return { ...set, _id: uid(), items: set.items.map(cloneItem) }; }
function cloneRound(round) { return { ...round, _id: uid(), sets: round.sets.map(cloneSet) }; }

function insertAfter(arr, id, newItem) {
  const i = arr.findIndex((x) => x._id === id);
  if (i < 0) return [...arr, newItem];
  return [...arr.slice(0, i + 1), newItem, ...arr.slice(i + 1)];
}

function move(arr, id, dir) {
  const i = arr.findIndex((x) => x._id === id);
  if (i < 0) return arr;
  const j = dir === 'up' ? i - 1 : i + 1;
  if (j < 0 || j >= arr.length) return arr;
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

// ─── serialize to API shape ───────────────────────────────────────────────────
function serialize(state) {
  const { meta, phases } = state;
  return {
    name: meta.name,
    description: meta.description || null,
    format: meta.format,
    duration: meta.duration ? Number(meta.duration) : null,
    difficulty: Number(meta.difficulty),
    published: meta.published,
    sounds_enabled: meta.sounds_enabled,
    apple_music_playlist_id: meta.apple_music_playlist_id || null,
    playlist_enabled: meta.playlist_enabled,
    tag_ids: meta.tag_ids,
    is_global: meta.is_global || false,
    is_free: meta.is_free || false,
    phases: phases.map((p, pi) => ({
      name: p.name,
      type: p.type,
      sort_order: pi,
      music_enabled: p.music_enabled !== false,
      rounds: p.rounds.map((r, ri) => ({
        name: r.name || null,
        repetitions: Number(r.repetitions) || 1,
        rest_between_reps: Number(r.rest_between_reps) || 0,
        sort_order: ri,
        sets: r.sets.map((s, si) => ({
          name: s.name || null,
          repetitions: Number(s.repetitions) || 1,
          rest_between_reps: Number(s.rest_between_reps) || 0,
          sort_order: si,
          items: s.items
            .filter((i) => i.exercise_id)
            .map((i, ii) => ({
              exercise_id: Number(i.exercise_id),
              duration: i.duration ? Number(i.duration) : null,
              repetitions: i.repetitions ? Number(i.repetitions) : null,
              rest_after: Number(i.rest_after) || 0,
              notes: i.notes || null,
              sort_order: ii,
            })),
        })),
      })),
    })),
  };
}

// ─── deserialize from API response ───────────────────────────────────────────
function deserialize(workout) {
  return {
    meta: {
      name: workout.name,
      description: workout.description || '',
      format: workout.format,
      duration: workout.duration || '',
      difficulty: workout.difficulty || 1,
      published: workout.published || false,
      sounds_enabled: workout.sounds_enabled !== false,
      apple_music_playlist_id: workout.apple_music_playlist_id || '',
      playlist_enabled: workout.playlist_enabled || false,
      tag_ids: workout.tags?.map((t) => t.id) || [],
      is_global: workout.is_global || false,
      is_free: workout.is_free || false,
    },
    phases: (workout.phases || []).map((p) => ({
      _id: uid(),
      serverId: p.id || null,
      name: p.name,
      type: p.type,
      music_enabled: p.music_enabled !== false,
      rounds: (p.rounds || []).map((r) => ({
        _id: uid(),
        name: r.name || '',
        repetitions: r.repetitions || 1,
        rest_between_reps: r.rest_between_reps || 0,
        sets: (r.sets || []).map((s) => ({
          _id: uid(),
          name: s.name || '',
          repetitions: s.repetitions || 1,
          rest_between_reps: s.rest_between_reps || 0,
          items: (s.items || []).map((i) => ({
            _id: uid(),
            exercise_id: i.exercise_id || '',
            duration: i.duration || 30,
            repetitions: i.repetitions || null,
            rest_after: i.rest_after || 0,
            notes: i.notes || '',
          })),
        })),
      })),
    })),
  };
}

// ─── Phase type labels ────────────────────────────────────────────────────────
const PHASE_TYPE_LABELS = PHASE_DEFAULT_NAMES;
const PHASE_TYPE_COLORS = { warmup: 'warning', workout: 'primary', cooldown: 'info' };

// ─── Main component ───────────────────────────────────────────────────────────
export default function WorkoutBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const { user } = useAuth();
  const musicEnabled = user?.music_integration_enabled;

  const [state, dispatch] = useReducer(builderReducer, { meta: defaultMeta, phases: [emptyPhase()] });
  const [exercises, setExercises] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [existingIntroMedia, setExistingIntroMedia] = useState([]);
  const [pendingIntroMedia, setPendingIntroMedia] = useState([]);
  const [openPhases, setOpenPhases] = useState({});  // _id → boolean
  const [defaultPhasesOpen, setDefaultPhasesOpen] = useState(true);

  // Apple Music Playlisten
  const [playlists, setPlaylists] = useState([]);
  const [playlistsLoading, setPlaylistsLoading] = useState(false);
  const appleMusicConnected = Boolean(localStorage.getItem('apple_music_user_token'));

  useEffect(() => {
    if (!musicEnabled || !appleMusicConnected) return;
    setPlaylistsLoading(true);
    getAppleDeveloperToken()
      .then(({ developer_token }) => {
        const userToken = localStorage.getItem('apple_music_user_token');
        return fetch('https://api.music.apple.com/v1/me/library/playlists?limit=100', {
          headers: {
            'Authorization': `Bearer ${developer_token}`,
            'Music-User-Token': userToken,
          },
        });
      })
      .then((r) => r.json())
      .then((data) => setPlaylists(data.data || []))
      .catch(() => setPlaylists([]))
      .finally(() => setPlaylistsLoading(false));
  }, [musicEnabled, appleMusicConnected]);

  useEffect(() => {
    const promises = [getExercises(), getTags(), getTenantSettings()];
    if (isEdit) promises.push(getWorkout(id));
    Promise.all(promises)
      .then(([ex, t, tenantData, workout]) => {
        setExercises(ex);
        setTags(t);
        const defaultOpen = !(tenantData?.config?.builder_phases_collapsed ?? false);
        setDefaultPhasesOpen(defaultOpen);
        if (workout) {
          const stored = id ? JSON.parse(localStorage.getItem(`mkw_builder_phases_${id}`) || 'null') : null;
          const deserialized = deserialize(workout);
          const openMap = {};
          deserialized.phases.forEach((phase, i) => {
            openMap[phase._id] = Array.isArray(stored) && stored[i] !== undefined ? stored[i] : defaultOpen;
          });
          setOpenPhases(openMap);
          dispatch({ type: 'LOAD', payload: deserialized });
          setExistingIntroMedia(workout.intro_media || []);
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const exerciseMap = Object.fromEntries(exercises.map((e) => [e.id, e]));

  function toggleTag(tagId) {
    const current = state.meta.tag_ids;
    dispatch({
      type: 'SET_META',
      payload: {
        tag_ids: current.includes(tagId) ? current.filter((t) => t !== tagId) : [...current, tagId],
      },
    });
    setIsDirty(true);
  }

  // Browser-Navigation-Guard (Tab schließen / Reload)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  function handleBack() {
    if (isDirty && !window.confirm('Du hast ungespeicherte Änderungen. Möchtest du wirklich verlassen?')) return;
    navigate('/workouts');
  }

  async function handleSave() {
    if (!state.meta.name.trim()) { setError('Bitte gib einen Namen ein.'); return; }
    setSaving(true);
    setError('');
    setSaveSuccess(false);
    try {
      const payload = serialize(state);
      let workoutId;
      if (isEdit) {
        await updateWorkout(id, payload);
        workoutId = Number(id);
      } else {
        const created = await createWorkout(payload);
        workoutId = created.id;
      }
      for (const item of pendingIntroMedia) {
        await addMediaToWorkout(workoutId, item.media.id, item.mediaType);
      }
      // Collapse-Zustand per Index sichern, bevor serverIds sich durch PUT neu vergeben
      const ordered = phases.map((p) => isPhaseOpen(p));
      localStorage.setItem(`mkw_builder_phases_${workoutId}`, JSON.stringify(ordered));

      if (!isEdit) {
        // Neues Workout: zur Edit-URL weiterleiten (einmalig)
        navigate(`/workouts/${workoutId}/edit`, { replace: true });
      } else {
        // Im Builder bleiben – Intro-Media neu laden, Dirty-State zurücksetzen
        const freshWorkout = await getWorkout(id);
        setExistingIntroMedia(freshWorkout.intro_media || []);
        setPendingIntroMedia([]);
        setIsDirty(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  const { meta, phases } = state;
  const D = (action) => { dispatch(action); setIsDirty(true); };

  function isPhaseOpen(phase) {
    return openPhases[phase._id] !== undefined ? openPhases[phase._id] : defaultPhasesOpen;
  }

  function togglePhase(phase) {
    const newOpen = !isPhaseOpen(phase);
    setOpenPhases((prev) => {
      const updated = { ...prev, [phase._id]: newOpen };
      if (id) {
        const ordered = phases.map((p) => (updated[p._id] !== undefined ? updated[p._id] : defaultPhasesOpen));
        localStorage.setItem(`mkw_builder_phases_${id}`, JSON.stringify(ordered));
      }
      return updated;
    });
  }

  function calcDurationFromPhases() {
    let totalSeconds = 0;
    for (const phase of phases) {
      for (const round of phase.rounds) {
        const roundReps = Number(round.repetitions) || 1;
        let roundBodySeconds = 0;
        for (const set of round.sets) {
          const setReps = Number(set.repetitions) || 1;
          const restBetweenSets = Number(set.rest_between_reps) || 0;
          let setBodySeconds = 0;
          for (const item of set.items) {
            setBodySeconds += (Number(item.duration) || 0) + (Number(item.rest_after) || 0);
          }
          roundBodySeconds += setBodySeconds * setReps + restBetweenSets * Math.max(setReps - 1, 0);
        }
        const restBetweenRounds = Number(round.rest_between_reps) || 0;
        totalSeconds += roundBodySeconds * roundReps + restBetweenRounds * Math.max(roundReps - 1, 0);
      }
    }
    return Math.ceil(totalSeconds / 60);
  }

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-4">
        <button className="btn btn-outline-secondary btn-sm" onClick={handleBack}>
          <i className="bi bi-arrow-left" />
        </button>
        <h4 className="mb-0 fw-bold">{isEdit ? 'Workout bearbeiten' : 'Neues Workout'}</h4>
        <div className="ms-auto d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={handleBack}>
            {isEdit ? 'Zurück' : 'Abbrechen'}
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <><span className="spinner-border spinner-border-sm me-2" />Speichern…</> : (
              <><i className="bi bi-check-lg me-1" />Speichern</>
            )}
          </button>
        </div>
      </div>

      <AlertBanner message={error} type="danger" onDismiss={() => setError('')} />
      {saveSuccess && (
        <div className="alert alert-success py-2 d-flex align-items-center gap-2 mb-3" role="alert">
          <i className="bi bi-check-circle-fill" />
          Workout gespeichert.
        </div>
      )}

      <div className="row g-4">
        {/* ── Left: Metadata ── */}
        <div className="col-lg-4">
          <div className="card mb-3 builder-meta-card">
            <div className="card-body">
              <h6 className="card-subtitle mb-3 text-muted fw-semibold">Grunddaten</h6>
              <div className="mb-3">
                <label className="form-label">Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={meta.name}
                  onChange={(e) => D({ type: 'SET_META', payload: { name: e.target.value } })}
                  placeholder="z.B. HIIT Montag"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Beschreibung</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={meta.description}
                  onChange={(e) => D({ type: 'SET_META', payload: { description: e.target.value } })}
                />
              </div>
              <div className="row g-2">
                <div className="col-6">
                  <label className="form-label">Format</label>
                  <select
                    className="form-select"
                    value={meta.format}
                    onChange={(e) => D({ type: 'SET_META', payload: { format: e.target.value } })}
                  >
                    <option value="custom">Custom</option>
                    <option value="tabata">Tabata</option>
                    <option value="hiit">HIIT</option>
                    <option value="circuit">Circuit</option>
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label">Dauer (Min.)</label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      min={1}
                      value={meta.duration}
                      onChange={(e) => D({ type: 'SET_META', payload: { duration: e.target.value } })}
                      placeholder="—"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      title="Dauer aus Phasen berechnen"
                      onClick={() => D({ type: 'SET_META', payload: { duration: calcDurationFromPhases() } })}
                    >
                      <i className="bi bi-calculator" />
                    </button>
                  </div>
                  <div className="form-text">Oder berechnen lassen</div>
                </div>
                <div className="col-12">
                  <label className="form-label">Schwierigkeit</label>
                  <select
                    className="form-select"
                    value={meta.difficulty}
                    onChange={(e) => D({ type: 'SET_META', payload: { difficulty: Number(e.target.value) } })}
                  >
                    {[1, 2, 3, 4, 5].map((d) => (
                      <option key={d} value={d}>{d} – {['Sehr leicht', 'Leicht', 'Mittel', 'Schwer', 'Sehr schwer'][d - 1]}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <div className="form-check mt-1">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="published"
                      checked={meta.published}
                      onChange={(e) => D({ type: 'SET_META', payload: { published: e.target.checked } })}
                    />
                    <label className="form-check-label" htmlFor="published">Direkt veröffentlichen</label>
                  </div>
                  <div className="form-check mt-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="sounds_enabled"
                      checked={meta.sounds_enabled}
                      onChange={(e) => D({ type: 'SET_META', payload: { sounds_enabled: e.target.checked } })}
                    />
                    <label className="form-check-label" htmlFor="sounds_enabled">
                      <i className="bi bi-volume-up me-1" />
                      Töne aktivieren (Beep, Box Counter &amp; Übergänge)
                    </label>
                  </div>

                  {user?.role === 'super_admin' && (
                    <div className="form-check form-switch mt-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="isGlobal"
                        checked={meta.is_global}
                        onChange={(e) => D({ type: 'SET_META', payload: { is_global: e.target.checked } })}
                      />
                      <label className="form-check-label" htmlFor="isGlobal">
                        <i className="bi bi-globe me-1 text-warning" />
                        Global veröffentlichen
                      </label>
                    </div>
                  )}

                  {user?.role === 'super_admin' && meta.is_global && (
                    <div className="form-check form-switch mt-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="isFree"
                        checked={meta.is_free}
                        onChange={(e) => D({ type: 'SET_META', payload: { is_free: e.target.checked } })}
                      />
                      <label className="form-check-label" htmlFor="isFree">
                        <i className="bi bi-unlock me-1 text-success" />
                        Umsonst (im Gratis-Tier ohne Login verfügbar)
                      </label>
                    </div>
                  )}

                  {musicEnabled && (
                    <div className="mt-2">
                      <div className="form-check mt-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="playlist_enabled"
                          checked={meta.playlist_enabled}
                          onChange={(e) => D({ type: 'SET_META', payload: {
                            playlist_enabled: e.target.checked,
                            apple_music_playlist_id: e.target.checked ? meta.apple_music_playlist_id : ''
                          }})}
                        />
                        <label className="form-check-label" htmlFor="playlist_enabled">
                          <i className="bi bi-music-note-list me-1" />
                          Playlist abspielen
                        </label>
                      </div>
                      {meta.playlist_enabled && (
                        <div className="mt-2">
                          <label className="form-label fw-semibold mb-1">
                            <i className="bi bi-music-note-beamed me-1" />
                            Apple Music Playlist
                          </label>
                          {!appleMusicConnected ? (
                            <div className="alert alert-warning py-2 mb-0 small">
                              <i className="bi bi-exclamation-triangle me-1" />
                              Nicht mit Apple Music verbunden. Bitte zuerst in den{' '}
                              <a href="/settings" target="_blank" rel="noreferrer">Einstellungen</a>{' '}
                              verbinden.
                            </div>
                          ) : playlistsLoading ? (
                            <div className="d-flex align-items-center gap-2 text-muted small">
                              <span className="spinner-border spinner-border-sm" />
                              Playlisten werden geladen…
                            </div>
                          ) : playlists.length > 0 ? (
                            <select
                              className="form-select"
                              value={meta.apple_music_playlist_id}
                              onChange={(e) => D({ type: 'SET_META', payload: { apple_music_playlist_id: e.target.value } })}
                            >
                              <option value="">— Keine Playlist —</option>
                              {playlists.map((pl) => (
                                <option key={pl.id} value={pl.id}>
                                  {pl.attributes?.name || pl.id}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Playlist-ID (z. B. p.abc123…)"
                              value={meta.apple_music_playlist_id}
                              onChange={(e) => D({ type: 'SET_META', payload: { apple_music_playlist_id: e.target.value } })}
                            />
                          )}
                          <div className="form-text">
                            Wird im Presenter automatisch gestartet.
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-3 builder-meta-card">
            <div className="card-body">
              <h6 className="card-subtitle mb-3 text-muted fw-semibold">Tags</h6>
              <TagSelector
                tags={tags}
                selectedIds={meta.tag_ids}
                onToggle={toggleTag}
                onTagCreated={(tag) => setTags((prev) => [...prev, tag])}
              />
            </div>
          </div>

          <div className="card builder-meta-card">
            <div className="card-body">
              <WorkoutIntroMediaSection
                existingMedia={existingIntroMedia}
                pendingMedia={pendingIntroMedia}
                onAddPending={(item) => setPendingIntroMedia((prev) => [...prev, item])}
                onRemoveExisting={(relId) => {
                  removeMediaFromWorkout(Number(id), relId).catch(() => {});
                  setExistingIntroMedia((prev) => prev.filter((m) => m.id !== relId));
                }}
                onRemovePending={(mediaId) => setPendingIntroMedia((prev) => prev.filter((m) => m.media.id !== mediaId))}
              />
            </div>
          </div>
        </div>

        {/* ── Right: Phases ── */}
        <div className="col-lg-8">
          {phases.map((phase, pi) => (
            <PhaseBlock
              key={phase._id}
              phase={phase}
              phaseIndex={pi}
              totalPhases={phases.length}
              exercises={exercises}
              exerciseMap={exerciseMap}
              dispatch={D}
              musicEnabled={musicEnabled}
              hasPlaylist={Boolean(meta.apple_music_playlist_id) && meta.playlist_enabled}
              isOpen={isPhaseOpen(phase)}
              onToggle={() => togglePhase(phase)}
            />
          ))}
          <button
            className="btn builder-add-phase-btn w-100 mt-2"
            onClick={() => D({ type: 'ADD_PHASE' })}
          >
            <i className="bi bi-plus-lg me-1" />
            Phase hinzufügen
          </button>
        </div>
      </div>

    </div>
  );
}

// ─── PhaseBlock ───────────────────────────────────────────────────────────────
function PhaseBlock({ phase, phaseIndex, totalPhases, exercises, exerciseMap, dispatch, musicEnabled, hasPlaylist, isOpen, onToggle }) {
  return (
    <div className="card mb-3 builder-phase-card" style={{ borderLeft: `4px solid var(--bs-${PHASE_TYPE_COLORS[phase.type] || 'secondary'})` }}>
      <div className="card-header d-flex align-items-center gap-2 py-2">
        <button className="btn btn-sm btn-link p-0" style={{ color: 'var(--color-text)' }} onClick={onToggle}>
          <i className={`bi ${isOpen ? 'bi-chevron-down' : 'bi-chevron-right'}`} />
        </button>
        <span className={`badge bg-${PHASE_TYPE_COLORS[phase.type] || 'secondary'}`}>
          {PHASE_TYPE_LABELS[phase.type] || phase.type}
        </span>
        <input
          className="form-control form-control-sm fw-semibold"
          style={{ maxWidth: 200 }}
          value={phase.name}
          onChange={(e) => dispatch({ type: 'UPDATE_PHASE', id: phase._id, payload: { name: e.target.value } })}
          placeholder="Phase benennen"
        />
        <select
          className="form-select form-select-sm"
          style={{ maxWidth: 130 }}
          value={phase.type}
          onChange={(e) => {
            const newType = e.target.value;
            const payload = { type: newType };
            // Auto-update name only if it still matches the old type's default
            if (phase.name === PHASE_DEFAULT_NAMES[phase.type]) {
              payload.name = PHASE_DEFAULT_NAMES[newType] || phase.name;
            }
            dispatch({ type: 'UPDATE_PHASE', id: phase._id, payload });
          }}
        >
          <option value="warmup">Aufwärmen</option>
          <option value="workout">Hauptteil</option>
          <option value="cooldown">Abkühlen</option>
        </select>
        {musicEnabled && hasPlaylist && (
          <button
            type="button"
            className={`btn btn-sm ${phase.music_enabled ? 'btn-success' : 'btn-outline-secondary'}`}
            title={phase.music_enabled ? 'Musik für diese Phase ein (klicken zum Deaktivieren)' : 'Musik für diese Phase aus (klicken zum Aktivieren)'}
            onClick={() => dispatch({ type: 'UPDATE_PHASE', id: phase._id, payload: { music_enabled: !phase.music_enabled } })}
          >
            <i className="bi bi-music-note-beamed" />
          </button>
        )}
        <div className="ms-auto d-flex gap-1">
          <button
            className="btn btn-sm btn-outline-secondary builder-btn-icon"
            disabled={phaseIndex === 0}
            onClick={() => dispatch({ type: 'MOVE_PHASE', id: phase._id, dir: 'up' })}
          ><i className="bi bi-arrow-up" /></button>
          <button
            className="btn btn-sm btn-outline-secondary builder-btn-icon"
            disabled={phaseIndex === totalPhases - 1}
            onClick={() => dispatch({ type: 'MOVE_PHASE', id: phase._id, dir: 'down' })}
          ><i className="bi bi-arrow-down" /></button>
          <button
            className="btn btn-sm btn-outline-danger builder-btn-icon"
            onClick={() => dispatch({ type: 'REMOVE_PHASE', id: phase._id })}
          ><i className="bi bi-trash" /></button>
        </div>
      </div>

      {isOpen && (
        <div className="card-body pt-2">
          {phase.rounds.map((round, ri) => (
            <RoundBlock
              key={round._id}
              round={round}
              roundIndex={ri}
              totalRounds={phase.rounds.length}
              phaseId={phase._id}
              exercises={exercises}
              exerciseMap={exerciseMap}
              dispatch={dispatch}
            />
          ))}
          <button
            className="btn btn-sm builder-add-btn w-100 mt-2"
            onClick={() => dispatch({ type: 'ADD_ROUND', phaseId: phase._id })}
          >
            <i className="bi bi-plus me-1" />Runde hinzufügen
          </button>
        </div>
      )}
    </div>
  );
}

// ─── RoundBlock ───────────────────────────────────────────────────────────────
function RoundBlock({ round, roundIndex, totalRounds, phaseId, exercises, exerciseMap, dispatch }) {
  return (
    <div className="builder-round p-3 mb-2">
      <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
        <span className="text-muted small fw-semibold" style={{ minWidth: 60 }}>Runde {roundIndex + 1}</span>
        <input
          className="form-control form-control-sm"
          style={{ maxWidth: 160 }}
          placeholder="Name (opt.)"
          value={round.name}
          onChange={(e) => dispatch({ type: 'UPDATE_ROUND', phaseId, id: round._id, payload: { name: e.target.value } })}
        />
        <div className="d-flex align-items-center gap-1">
          <label className="small text-muted me-1 mb-0">Wdh.</label>
          <input
            type="number"
            className="form-control form-control-sm"
            style={{ width: 60 }}
            min={1}
            value={round.repetitions}
            onChange={(e) => dispatch({ type: 'UPDATE_ROUND', phaseId, id: round._id, payload: { repetitions: e.target.value } })}
          />
        </div>
        <div className="d-flex align-items-center gap-1">
          <label className="small text-muted me-1 mb-0">Pause (s)</label>
          <input
            type="number"
            className="form-control form-control-sm"
            style={{ width: 70 }}
            min={0}
            value={round.rest_between_reps}
            onChange={(e) => dispatch({ type: 'UPDATE_ROUND', phaseId, id: round._id, payload: { rest_between_reps: e.target.value } })}
          />
        </div>
        <div className="ms-auto d-flex gap-1">
          <button className="btn btn-sm btn-outline-secondary builder-btn-icon" disabled={roundIndex === 0}
            onClick={() => dispatch({ type: 'MOVE_ROUND', phaseId, id: round._id, dir: 'up' })}>
            <i className="bi bi-arrow-up" />
          </button>
          <button className="btn btn-sm btn-outline-secondary builder-btn-icon" disabled={roundIndex === totalRounds - 1}
            onClick={() => dispatch({ type: 'MOVE_ROUND', phaseId, id: round._id, dir: 'down' })}>
            <i className="bi bi-arrow-down" />
          </button>
          <button className="btn btn-sm btn-outline-secondary builder-btn-icon" title="Runde duplizieren"
            onClick={() => dispatch({ type: 'DUPLICATE_ROUND', phaseId, id: round._id })}>
            <i className="bi bi-copy" />
          </button>
          <button className="btn btn-sm btn-outline-danger builder-btn-icon"
            onClick={() => dispatch({ type: 'REMOVE_ROUND', phaseId, id: round._id })}>
            <i className="bi bi-trash" />
          </button>
        </div>
      </div>

      {round.sets.map((set, si) => (
        <SetBlock
          key={set._id}
          set={set}
          setIndex={si}
          totalSets={round.sets.length}
          phaseId={phaseId}
          roundId={round._id}
          exercises={exercises}
          exerciseMap={exerciseMap}
          dispatch={dispatch}
        />
      ))}
      <button
        className="btn btn-sm builder-add-btn w-100 mt-2"
        onClick={() => dispatch({ type: 'ADD_SET', phaseId, roundId: round._id })}
      >
        <i className="bi bi-plus me-1" />Set hinzufügen
      </button>
    </div>
  );
}

// ─── SetBlock ─────────────────────────────────────────────────────────────────
function SetBlock({ set, setIndex, totalSets, phaseId, roundId, exercises, exerciseMap, dispatch }) {
  return (
    <div className="builder-set p-3 mb-2 ms-3">
      <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
        <span className="text-muted small" style={{ minWidth: 50 }}>Set {setIndex + 1}</span>
        <input
          className="form-control form-control-sm"
          style={{ maxWidth: 140 }}
          placeholder="Name (opt.)"
          value={set.name}
          onChange={(e) => dispatch({ type: 'UPDATE_SET', phaseId, roundId, id: set._id, payload: { name: e.target.value } })}
        />
        <div className="d-flex align-items-center gap-1">
          <label className="small text-muted mb-0">Wdh.</label>
          <input type="number" className="form-control form-control-sm" style={{ width: 60 }} min={1}
            value={set.repetitions}
            onChange={(e) => dispatch({ type: 'UPDATE_SET', phaseId, roundId, id: set._id, payload: { repetitions: e.target.value } })}
          />
        </div>
        <div className="ms-auto d-flex gap-1">
          <button className="btn btn-sm btn-outline-secondary builder-btn-icon" disabled={setIndex === 0}
            onClick={() => dispatch({ type: 'MOVE_SET', phaseId, roundId, id: set._id, dir: 'up' })}>
            <i className="bi bi-arrow-up" />
          </button>
          <button className="btn btn-sm btn-outline-secondary builder-btn-icon" disabled={setIndex === totalSets - 1}
            onClick={() => dispatch({ type: 'MOVE_SET', phaseId, roundId, id: set._id, dir: 'down' })}>
            <i className="bi bi-arrow-down" />
          </button>
          <button className="btn btn-sm btn-outline-secondary builder-btn-icon" title="Set duplizieren"
            onClick={() => dispatch({ type: 'DUPLICATE_SET', phaseId, roundId, id: set._id })}>
            <i className="bi bi-copy" />
          </button>
          <button className="btn btn-sm btn-outline-danger builder-btn-icon"
            onClick={() => dispatch({ type: 'REMOVE_SET', phaseId, roundId, id: set._id })}>
            <i className="bi bi-trash" />
          </button>
        </div>
      </div>

      <table className="table table-sm table-borderless builder-exercise-table mb-1">
        <thead>
          <tr>
            <th style={{ minWidth: 200 }}>Übung</th>
            <th style={{ width: 80 }}>Dauer (s)</th>
            <th style={{ width: 70 }}>Wdh.</th>
            <th style={{ width: 80 }}>Pause (s)</th>
            <th style={{ width: 120 }}>Notizen</th>
            <th style={{ width: 80 }}></th>
          </tr>
        </thead>
        <tbody>
          {set.items.map((item, ii) => (
            <ItemRow
              key={item._id}
              item={item}
              itemIndex={ii}
              totalItems={set.items.length}
              phaseId={phaseId}
              roundId={roundId}
              setId={set._id}
              exercises={exercises}
              exerciseMap={exerciseMap}
              dispatch={dispatch}
            />
          ))}
        </tbody>
      </table>
      <button
        className="btn btn-sm builder-add-btn mt-1"
        onClick={() => dispatch({ type: 'ADD_ITEM', phaseId, roundId, setId: set._id })}
      >
        <i className="bi bi-plus me-1" />Übung hinzufügen
      </button>
    </div>
  );
}

// ─── ItemRow ─────────────────────────────────────────────────────────────────
function ItemRow({ item, itemIndex, totalItems, phaseId, roundId, setId, exercises, dispatch }) {
  return (
    <tr className="builder-item-row">
      <td className="builder-item-exercise">
        <select
          className="form-select form-select-sm"
          value={item.exercise_id}
          onChange={(e) => dispatch({ type: 'UPDATE_ITEM', phaseId, roundId, setId, id: item._id, payload: { exercise_id: e.target.value } })}
        >
          <option value="">— Übung wählen —</option>
          {exercises.map((ex) => (
            <option key={ex.id} value={ex.id}>{ex.name}</option>
          ))}
        </select>
      </td>
      <td className="builder-item-duration">
        <input type="number" className="form-control form-control-sm" min={0}
          value={item.duration ?? ''}
          placeholder="—"
          onChange={(e) => dispatch({ type: 'UPDATE_ITEM', phaseId, roundId, setId, id: item._id, payload: { duration: e.target.value || null } })}
        />
      </td>
      <td className="builder-item-reps">
        <input type="number" className="form-control form-control-sm" min={1}
          value={item.repetitions ?? ''}
          placeholder="—"
          onChange={(e) => dispatch({ type: 'UPDATE_ITEM', phaseId, roundId, setId, id: item._id, payload: { repetitions: e.target.value || null } })}
        />
      </td>
      <td className="builder-item-rest">
        <input type="number" className="form-control form-control-sm" min={0}
          value={item.rest_after ?? 0}
          onChange={(e) => dispatch({ type: 'UPDATE_ITEM', phaseId, roundId, setId, id: item._id, payload: { rest_after: e.target.value } })}
        />
      </td>
      <td className="builder-item-notes">
        <input type="text" className="form-control form-control-sm"
          value={item.notes}
          placeholder="—"
          onChange={(e) => dispatch({ type: 'UPDATE_ITEM', phaseId, roundId, setId, id: item._id, payload: { notes: e.target.value } })}
        />
      </td>
      <td className="builder-item-actions">
        <div className="d-flex gap-1">
          <button className="btn btn-sm btn-outline-secondary builder-btn-icon" disabled={itemIndex === 0}
            onClick={() => dispatch({ type: 'MOVE_ITEM', phaseId, roundId, setId, id: item._id, dir: 'up' })}>
            <i className="bi bi-arrow-up" />
          </button>
          <button className="btn btn-sm btn-outline-secondary builder-btn-icon" disabled={itemIndex === totalItems - 1}
            onClick={() => dispatch({ type: 'MOVE_ITEM', phaseId, roundId, setId, id: item._id, dir: 'down' })}>
            <i className="bi bi-arrow-down" />
          </button>
          <button className="btn btn-sm btn-outline-danger builder-btn-icon"
            onClick={() => dispatch({ type: 'REMOVE_ITEM', phaseId, roundId, setId, id: item._id })}>
            <i className="bi bi-trash" />
          </button>
        </div>
      </td>
    </tr>
  );
}
